import { useEffect } from "react";
import { motion } from "framer-motion";

const CURTAIN = [0.76, 0, 0.24, 1];
export const INTRO_DURATION = 2.5; // seconds until the curtain starts lifting

export const CinematicIntro = ({ letters = "SS", name = "", title = "", onDone }) => {
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const t = setTimeout(onDone, INTRO_DURATION * 1000);
    return () => {
      clearTimeout(t);
      document.body.style.overflow = prev;
    };
  }, [onDone]);

  const chars = name.toUpperCase().split("");

  return (
    <>
      {/* back layer: soft pink curtain that lifts slightly later for depth */}
      <motion.div
        aria-hidden="true"
        className="fixed inset-0 z-[99] bg-[hsl(345_70%_90%)]"
        initial={{ y: 0 }}
        exit={{ y: "-100%", transition: { duration: 1.05, ease: CURTAIN, delay: 0.12 } }}
      />
      <motion.div
        data-testid="cinematic-intro"
        role="presentation"
        onClick={onDone}
        className="fixed inset-0 z-[100] flex cursor-pointer flex-col items-center justify-center bg-[hsl(345_58%_13%)] px-6 text-[hsl(350_60%_97%)]"
        initial={{ y: 0 }}
        exit={{ y: "-100%", transition: { duration: 0.95, ease: CURTAIN } }}
      >
        <motion.div exit={{ opacity: 0, y: -30, transition: { duration: 0.5, ease: CURTAIN } }} className="flex flex-col items-center">
          <div className="relative flex h-28 w-28 items-center justify-center sm:h-32 sm:w-32">
            <svg viewBox="0 0 100 100" className="absolute inset-0 h-full w-full -rotate-90" aria-hidden="true">
              <motion.circle
                cx="50"
                cy="50"
                r="47"
                fill="none"
                stroke="hsl(345 80% 84%)"
                strokeWidth="0.8"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 1.1, ease: [0.65, 0, 0.35, 1] }}
              />
              <motion.circle
                cx="50"
                cy="50"
                r="42"
                fill="none"
                stroke="hsl(345 80% 84% / 0.35)"
                strokeWidth="0.5"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 1.1, ease: [0.65, 0, 0.35, 1], delay: 0.15 }}
              />
            </svg>
            <motion.span
              initial={{ opacity: 0, scale: 0.85, filter: "blur(6px)" }}
              animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.45 }}
              className="font-display text-4xl !tracking-[-0.04em] sm:text-5xl"
            >
              {letters}
            </motion.span>
          </div>

          <div className="mt-9 flex flex-wrap justify-center overflow-hidden" aria-label={name}>
            {chars.map((ch, i) => (
              <motion.span
                key={i}
                initial={{ y: "110%", opacity: 0 }}
                animate={{ y: "0%", opacity: 1 }}
                transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: 0.8 + i * 0.035 }}
                className="inline-block text-sm font-bold tracking-[0.42em] sm:text-base"
              >
                {ch === " " ? "\u00a0" : ch}
              </motion.span>
            ))}
          </div>

          <motion.span
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 0.9, ease: [0.65, 0, 0.35, 1], delay: 1.35 }}
            className="mt-6 block h-px w-24 origin-center bg-[hsl(345_80%_84%)]"
          />
          {title && (
            <motion.p
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 0.8, y: 0 }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 1.55 }}
              className="mt-5 text-[10px] font-semibold uppercase tracking-[0.32em] text-[hsl(345_40%_86%)] sm:text-[11px]"
            >
              {title}
            </motion.p>
          )}
        </motion.div>
      </motion.div>
    </>
  );
};
